/**
 * App Debug Mode Service
 *
 * Manages the "nopressure:app-debug" localStorage flag and exposes
 * an `isActive()` check that other modules can use to gate debug UI/logging.
 *
 * Storage writes route through the central `safeLocalStorage` service so the
 * invite-link token tripwire (and the no-direct-localstorage inspection) is
 * never bypassed. This module must NOT import `logger` directly: `logger`
 * imports `appDebugService.isActive()`, so a direct edge would form a module-load
 * cycle. `safeLocalStorage` is safe to import because it pulls `logger` only via a
 * lazy dynamic import, so no static cycle is formed; `@/lib/env` imports nothing at all.
 *
 * Not built on `createLocalStorageFlag`: `get()` / `isExplicitlyEnabled()` exceed that interface.
 */
import { isNonProduction } from '@/lib/env'
import { safeLocalStorage } from '@/services/safeLocalStorage'

const STORAGE_KEY = 'nopressure:app-debug'

export const appDebugService = {
  set(status: boolean): void {
    safeLocalStorage.set(STORAGE_KEY, String(status))
  },

  get(): boolean {
    return safeLocalStorage.get(STORAGE_KEY) === 'true'
  },

  clear(): void {
    safeLocalStorage.remove(STORAGE_KEY)
  },

  /**
   * Debug mode resolution:
   *
   * | localStorage       | non-prod | prod |
   * |--------------------|----------|------|
   * | absent (null)      | ON       | OFF  |
   * | "true"             | ON       | ON   |
   * | "false"            | OFF      | OFF  |
   */
  isActive(): boolean {
    const stored = safeLocalStorage.get(STORAGE_KEY)
    if (stored !== null) return stored === 'true'
    return isNonProduction()
  },

  /**
   * Strict check: only true when the user has explicitly set the flag to "true".
   * Unlike isActive(), does NOT default to ON in non-prod environments.
   */
  isExplicitlyEnabled(): boolean {
    return safeLocalStorage.get(STORAGE_KEY) === 'true'
  },
}
